import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import QuoteForm from '../components/QuoteForm';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { Phone, Mail, Clock, CheckCircle, Star, Shield, Award, Clock4, X, MapPin } from 'lucide-react';
import { services, testimonials, companyInfo, whyChooseUs, serviceAreas, faqs, pricingCategories } from '../mock';
import {
  ReactCompareSlider,
  ReactCompareSliderImage
} from 'react-compare-slider';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LandingPage = () => {
  const [showPopup, setShowPopup] = useState(false);
  const [galleryPhotos, setGalleryPhotos] = useState([]);

  useEffect(() => {
    const popupTimer = setTimeout(() => {
      setShowPopup(true);
    }, 4000);
    return () => clearTimeout(popupTimer);
  }, []);

  useEffect(() => {
    const fetchGalleryImages = async () => {
      try {
        const response = await axios.get(`${API}/gallery`);
        setGalleryPhotos(response.data);
      } catch (error) {
        console.error('Error fetching gallery images:', error);
      }
    };
    fetchGalleryImages();
  }, []);

  const categoryLabel = (category) =>
    category === 'before-after' ? 'Before & After' :
    category === 'carpet' ? 'Carpet' :
    category === 'upholstery' ? 'Upholstery' :
    category === 'rug' ? 'Area Rug' :
    category === 'commercial' ? 'Commercial' : category;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Popup Offer */}
      {showPopup && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 animate-in fade-in duration-300">
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowPopup(false)}
          ></div>
          
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-auto overflow-hidden animate-in zoom-in-95 duration-300">
            <button
              onClick={() => setShowPopup(false)}
              className="absolute top-3 right-3 z-10 bg-slate-100 hover:bg-slate-200 rounded-full p-2 transition-colors"
              aria-label="Close popup"
              data-testid="popup-close-btn"
            >
              <X className="h-5 w-5 text-slate-600" />
            </button>
            
            <div className="bg-gradient-to-r from-cyan-600 to-blue-600 px-6 py-4 text-center">
              <Badge className="bg-white/20 text-white border-white/30 mb-2">Spring Special</Badge>
              <h2 className="text-3xl md:text-4xl font-bold text-white">3 Rooms for Only $125</h2>
              <p className="text-cyan-100 mt-1">Professional steam carpet cleaning for your Calgary home</p>
            </div>
            
            <div className="p-6">
              <div className="flex justify-center gap-3 mb-6">
                <span className="bg-cyan-100 text-cyan-700 px-4 py-2 rounded-full text-sm font-semibold">
                  FREE Vacuuming
                </span>
                <span className="bg-cyan-100 text-cyan-700 px-4 py-2 rounded-full text-sm font-semibold">
                  FREE Deodorizing
                </span>
              </div>
              
              <div className="space-y-3 mb-6">
                <div className="flex items-center text-slate-700">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                  <span className="text-sm">100% Satisfaction Guaranteed — we re-clean for free</span>
                </div>
                <div className="flex items-center text-slate-700">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                  <span className="text-sm">Safe for kids, pets & allergies</span>
                </div>
                <div className="flex items-center text-slate-700">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                  <span className="text-sm">No hidden fees — price you see is what you pay</span>
                </div>
              </div>
              
              <a 
                href="/#contact" 
                onClick={() => setShowPopup(false)}
                className="block"
              >
                <Button className="w-full bg-cyan-600 hover:bg-cyan-700 text-white font-bold py-4 text-lg shadow-lg" data-testid="popup-claim-btn">
                  Claim This Offer
                </Button>
              </a>
              
              <p className="text-center text-slate-500 text-xs mt-4">
                Limited time offer. Terms apply.
              </p>
            </div>
          </div>
        </div>
      )}

      <Header />

      {/* Hero Section */}
      <section id="home" className="relative py-16 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_spotless-floors-4/artifacts/hhzavyve_image.png"
            alt="Professional carpet cleaning"
            className="w-full h-full object-cover opacity-75"
          />
          <div className="absolute inset-0 bg-white/40"></div>
        </div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4 bg-cyan-100 text-cyan-700 hover:bg-cyan-100 text-base px-4 py-2">✨ Calgary's Premier Carpet Cleaning Service</Badge>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-6 leading-tight">
                Professional Carpet Cleaning in Calgary
              </h1>
              <p className="text-lg md:text-xl text-slate-700 mb-8 leading-relaxed">
                Looking for affordable carpet cleaning in Calgary AB? Transform your carpets, upholstery, and rugs with our expert carpet cleaning services in Calgary. Same-day availability, eco-friendly solutions, and 100% satisfaction guaranteed.
              </p>
              <div className="grid grid-cols-2 gap-4 text-sm text-slate-700 mb-8">
                <div className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-cyan-600 mr-2 flex-shrink-0" />
                  <span className="font-medium">Same-Day Service</span>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-cyan-600 mr-2 flex-shrink-0" />
                  <span className="font-medium">Eco-Friendly Supplies</span>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-cyan-600 mr-2 flex-shrink-0" />
                  <span className="font-medium">Professional Equipment</span>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-cyan-600 mr-2 flex-shrink-0" />
                  <span className="font-medium">Satisfaction Guaranteed</span>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <a 
                  href="tel:8255837478"
                  className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-xl px-6 py-3 text-center shadow-lg transition-colors"
                  data-testid="hero-call-btn"
                >
                  <p className="text-sm mb-0.5">Call Now for a Free Quote</p>
                  <p className="text-xl font-bold flex items-center justify-center gap-2">
                    <Phone className="h-5 w-5" />
                    (825) 583-7478
                  </p>
                </a>
                <a href="/#pricing" className="flex">
                  <Button variant="outline" className="border-cyan-600 text-cyan-600 hover:bg-cyan-50 font-semibold px-8 py-4 text-lg h-auto w-full" data-testid="hero-view-pricing-btn">
                    View Pricing
                  </Button>
                </a>
              </div>
            </div>

            {/* Hero Quote Form */}
            <div className="bg-white rounded-2xl shadow-2xl p-6 md:p-8" data-testid="hero-form-card">
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-slate-900">Request a Service</h2>
                <p className="text-slate-600 text-sm mt-1">Fill out the form to get started</p>
              </div>
              <QuoteForm formType="hero" submitLabel="Submit Request" />
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section id="why-us" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-cyan-100 text-cyan-700 hover:bg-cyan-100">Why Choose Us</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Calgary's Trusted Carpet Cleaning Experts</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Professional carpet cleaning in Calgary services you can trust - affordable, reliable, and guaranteed results
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((item) => (
              <div key={item.id} className="text-center">
                <div className="bg-cyan-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  {item.id === 1 && <Award className="h-8 w-8 text-cyan-600" />}
                  {item.id === 2 && <CheckCircle className="h-8 w-8 text-cyan-600" />}
                  {item.id === 3 && <Shield className="h-8 w-8 text-cyan-600" />}
                  {item.id === 4 && <Clock4 className="h-8 w-8 text-cyan-600" />}
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">{item.title}</h3>
                <p className="text-slate-600">{item.description}</p>
              </div>
            ))}
          </div>

          {/* Satisfaction Guarantee */}
          <div className="mt-16 bg-gradient-to-r from-cyan-600 to-blue-600 rounded-3xl p-8 md:p-12 text-center" data-testid="guarantee-section">
            <Shield className="h-14 w-14 text-white mx-auto mb-4" />
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">100% Satisfaction Guaranteed</h2>
            <p className="text-lg text-cyan-100 max-w-3xl mx-auto mb-8">
              We stand behind every job we complete. If you're not completely satisfied with our cleaning services, we'll return and re-clean any areas at no additional cost. Your happiness is our success.
            </p>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white/10 rounded-xl p-6">
                <h3 className="text-xl font-bold text-white mb-2">No Risk</h3>
                <p className="text-cyan-100 text-sm">Try our services risk-free — not satisfied? We'll re-clean for free!</p>
              </div>
              <div className="bg-white/10 rounded-xl p-6">
                <h3 className="text-xl font-bold text-white mb-2">Quality Promise</h3>
                <p className="text-cyan-100 text-sm">Professional results every time, backed by our guarantee</p>
              </div>
              <div className="bg-white/10 rounded-xl p-6">
                <h3 className="text-xl font-bold text-white mb-2">Trusted Service</h3>
                <p className="text-cyan-100 text-sm">Hundreds of satisfied customers across Calgary</p>
              </div>
            </div>
            <a href="/#contact">
              <Button className="bg-white text-cyan-600 hover:bg-cyan-50 font-bold px-8 py-4 text-lg shadow-lg" data-testid="guarantee-cta-btn">
                Get Your Free Quote Today
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-cyan-100 text-cyan-700 hover:bg-cyan-100">Our Services</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Professional Carpet Cleaning in Calgary Services</h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              From residential homes to commercial spaces, we offer affordable carpet cleaning in Calgary AB with specialized services tailored to your needs.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => (
              <Card key={service.id} className="border-slate-200 hover:shadow-xl transition-shadow duration-300 flex flex-col" data-testid={`service-card-${service.id}`}>
                <CardContent className="pt-6 flex flex-col flex-1">
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{service.title}</h3>
                  <p className="text-slate-600 mb-5">{service.description}</p>
                  <ul className="space-y-2 mb-6 flex-1">
                    {service.features.map((feature, i) => (
                      <li key={i} className="flex items-center text-slate-700 text-sm">
                        <CheckCircle className="h-4 w-4 text-cyan-600 mr-2 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <a href="/#contact" className="block">
                    <Button className="w-full bg-cyan-700 hover:bg-cyan-800 text-white font-semibold">
                      Book Now
                    </Button>
                  </a>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-cyan-100 text-cyan-700 hover:bg-cyan-100">Pricing</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Our Carpet Cleaning Prices Calgary</h2>
            <p className="text-lg text-slate-600">Transparent pricing. No hidden fees. No surprises.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {pricingCategories.map((category) => (
              <Card key={category.title} className="border-slate-200 shadow-lg overflow-hidden flex flex-col" data-testid={`pricing-card-${category.title.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                <div className={`${category.headerClass} px-6 py-5 text-center`}>
                  <h3 className="text-xl font-bold text-white">{category.title}</h3>
                </div>
                <CardContent className="pt-6 flex flex-col flex-1">
                  <ul className="space-y-3 flex-1">
                    {category.items.map((item) => (
                      <li key={item.name} className="flex justify-between items-center border-b border-slate-100 pb-2 text-sm">
                        <span className="text-slate-700">{item.name}</span>
                        <span className="font-bold text-slate-900 whitespace-nowrap ml-2">{item.price}</span>
                      </li>
                    ))}
                  </ul>
                  {category.note && (
                    <p className="text-xs text-slate-500 mt-3 italic">{category.note}</p>
                  )}
                  <a href="/#contact" className="block mt-6">
                    <Button className="w-full bg-cyan-600 hover:bg-cyan-700 text-white font-semibold">
                      Book Now
                    </Button>
                  </a>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="mt-10 text-center bg-slate-50 rounded-2xl p-6 max-w-3xl mx-auto">
            <p className="text-slate-700">
              <span className="font-semibold">Minimum charge $125.</span> Heavily soiled carpets or severe pet accidents may have additional charges. Free quote available — call us today!
            </p>
            <a href="tel:8255837478" className="inline-flex items-center gap-2 mt-3 text-cyan-600 font-bold text-xl hover:text-cyan-700">
              <Phone className="h-5 w-5" />
              (825) 583-7478
            </a>
          </div>
        </div>
      </section>

      {/* Gallery Section - Before & After */}
      <section id="gallery" className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-cyan-100 text-cyan-700 hover:bg-cyan-100">Our Work</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Before & After Results</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              See the amazing transformations from our professional carpet cleaning services
            </p>
          </div>
          
          <div className="space-y-12">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="relative rounded-xl overflow-hidden shadow-2xl">
                <ReactCompareSlider
                  itemOne={
                    <ReactCompareSliderImage
                      src="https://customer-assets.emergentagent.com/job_spotless-floors-4/artifacts/tb4snix2_WhatsApp%20Image%202026-02-13%20at%207.11.58%20PM%20%281%29.jpeg"
                      alt="After carpet cleaning"
                      style={{ objectFit: 'cover', width: '100%', height: '300px' }}
                    />
                  }
                  itemTwo={
                    <ReactCompareSliderImage
                      src="https://customer-assets.emergentagent.com/job_spotless-floors-4/artifacts/4aqxtmwb_WhatsApp%20Image%202026-02-13%20at%207.11.58%20PM.jpeg"
                      alt="Before carpet cleaning"
                      style={{ objectFit: 'cover', width: '100%', height: '300px' }}
                    />
                  }
                  position={50}
                  style={{ width: '100%', height: '300px' }}
                />
              </div>
              <div className="relative rounded-xl overflow-hidden shadow-2xl">
                <ReactCompareSlider
                  itemOne={
                    <ReactCompareSliderImage
                      src="https://customer-assets.emergentagent.com/job_spotless-floors-4/artifacts/cnogthe4_WhatsApp%20Image%202026-02-21%20at%203.36.21%20PM.jpeg"
                      alt="Before carpet cleaning"
                      style={{ objectFit: 'cover', width: '100%', height: '300px', transform: 'scaleX(-1)' }}
                    />
                  }
                  itemTwo={
                    <ReactCompareSliderImage
                      src="https://customer-assets.emergentagent.com/job_spotless-floors-4/artifacts/s2ojs0av_WhatsApp%20Image%202026-02-21%20at%203.36.21%20PM%20%281%29.jpeg"
                      alt="After carpet cleaning"
                      style={{ objectFit: 'cover', width: '100%', height: '300px', transform: 'scaleX(-1)' }}
                    />
                  }
                  position={50}
                  style={{ width: '100%', height: '300px' }}
                />
              </div>
            </div>

            {/* Dynamic Photo Gallery from Admin Uploads */}
            {galleryPhotos.length > 0 && (
              <div className="mt-12" data-testid="dynamic-gallery">
                <h3 className="text-2xl font-bold text-slate-900 text-center mb-8">More of Our Work</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {galleryPhotos.map((photo) => (
                    <div key={photo.id} className="relative group overflow-hidden rounded-xl shadow-lg">
                      <img
                        src={photo.secure_url}
                        alt={photo.title || 'Carpet cleaning result'}
                        loading="lazy"
                        className="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-110"
                      />
                      <div className="absolute bottom-3 left-3">
                        <Badge className="bg-cyan-600 text-white text-xs">
                          {categoryLabel(photo.category)}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-cyan-100 text-cyan-700 hover:bg-cyan-100">Testimonials</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">What Our Customers Say</h2>
            <p className="text-lg text-slate-600">
              Don't just take our word for it - hear from our satisfied clients
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="border-slate-200 hover:shadow-lg transition-shadow duration-300">
                <CardContent className="pt-6">
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 text-yellow-400 fill-yellow-400" />
                    ))}
                  </div>
                  <p className="text-slate-700 mb-6 italic">"{testimonial.content}"</p>
                  <div>
                    <p className="font-semibold text-slate-900">{testimonial.name}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faqs" className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-cyan-100 text-cyan-700 hover:bg-cyan-100">FAQ</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-lg text-slate-600">
              Everything you need to know about our carpet cleaning services
            </p>
          </div>
          <Accordion type="single" collapsible className="space-y-4" data-testid="faq-accordion">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`faq-${index}`} 
                className="bg-white rounded-xl border border-slate-200 px-6 shadow-sm"
              >
                <AccordionTrigger className="text-left font-semibold text-slate-900 hover:no-underline">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Areas We Serve Section */}
      <section id="location" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-cyan-100 text-cyan-700 hover:bg-cyan-100">Areas We Serve</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Carpet Cleaning Services Across Calgary & Surrounding Areas</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              We come to you — serving all Calgary neighbourhoods and surrounding communities
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-3" data-testid="service-areas-list">
            {serviceAreas.map((area) => (
              <span 
                key={area} 
                className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 text-slate-700 px-4 py-2 rounded-full text-sm font-medium hover:border-cyan-300 hover:bg-cyan-50 transition-colors"
              >
                <MapPin className="h-3.5 w-3.5 text-cyan-600" />
                {area}
              </span>
            ))}
          </div>
          <div className="text-center mt-10">
            <p className="text-slate-600 mb-4">Don't see your area? Call us — we likely serve you too!</p>
            <a href="/#contact">
              <Button className="bg-cyan-600 hover:bg-cyan-700 text-white font-bold px-8 py-4 text-lg shadow-lg" data-testid="areas-book-now-btn">
                Book Now
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-cyan-100 text-cyan-700 hover:bg-cyan-100">Get In Touch</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Book Affordable Carpet Cleaning in Calgary AB</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Request your free quote for professional carpet cleaning in Calgary. We'll get back to you within 24 hours with a personalized estimate.
            </p>
          </div>
          <div className="grid lg:grid-cols-2 gap-12">
            <div className="space-y-6">
              <h3 className="text-2xl font-bold text-slate-900">Contact Information</h3>
              <a 
                href="tel:8255837478"
                className="flex items-center p-5 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow"
                data-testid="contact-phone-card"
              >
                <div className="bg-cyan-600 p-3 rounded-lg mr-4">
                  <Phone className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900">Phone</h4>
                  <p className="text-cyan-600 font-bold text-lg">{companyInfo.phone}</p>
                </div>
              </a>
              <a 
                href={`mailto:${companyInfo.email}`}
                className="flex items-center p-5 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow"
                data-testid="contact-email-card"
              >
                <div className="bg-cyan-600 p-3 rounded-lg mr-4">
                  <Mail className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900">Email</h4>
                  <p className="text-slate-700">{companyInfo.email}</p>
                </div>
              </a>
              <div className="flex items-center p-5 bg-white rounded-xl shadow-sm" data-testid="contact-hours-card">
                <div className="bg-cyan-600 p-3 rounded-lg mr-4">
                  <Clock className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900">Hours</h4>
                  <p className="text-slate-700">{companyInfo.hours}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8" data-testid="contact-form-card">
              <QuoteForm formType="contact" submitLabel="Submit Booking Request" />
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LandingPage;
